import { NotificationDeadline } from 'api/types/UserEmployeeInfo';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type DeadlineContextValue = {
  deadlines: NotificationDeadline[];
  setDeadlines: (d: NotificationDeadline[]) => void;
  hydrated: boolean;
};
const DeadlineContext = createContext<DeadlineContextValue | null>(null);

const STORAGE_KEY = 'app:employeeKioskSelectedUser';

export function DeadlineProvider({ children }: { children: React.ReactNode }) {
  const [deadlines, setDeadlinesState] = useState<NotificationDeadline[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as NotificationDeadline[];
        if (Array.isArray(parsed)) setDeadlinesState(parsed);
      }
    } catch {
      setDeadlinesState([]);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setHydrated(true);
    }
  }, []);

  const setDeadlines = (d: NotificationDeadline[]) => {
    setDeadlinesState(d);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  };

  const value = useMemo(
    () => ({ deadlines, setDeadlines, hydrated }),
    [deadlines, hydrated]
  );

  return (
    <DeadlineContext.Provider value={value}>
      {children}
    </DeadlineContext.Provider>
  );
}

export function useDeadlines() {
  const ctx = useContext(DeadlineContext);
  if (!ctx)
    throw new Error('useDeadlines must be used within DeadlineProvider');
  return ctx;
}
